import { useCallback, useMemo, useState } from "react";

import { useCurrentStateAndParams } from "@uirouter/react";

import { openBillingInformationPage } from "app/common/hrefs";
import { swSettings } from "app/common/services/swSettings";
import { shouldSeeReactivationBanner } from "app/common/utils";
import { DUNNINGS_MODAL_LOCAL_STORAGE_KEY } from "app/components/Dunnings/dunningsConstants";
import { isOnPage, CI_PAGE_COLLECTION } from "app/components/PageTopBanner/pageCollections";
import { useLocalStorage } from "app/pages/activation/hooks/helpers/useLocalStorage";
import { allTrackers } from "app/services/track/track";
import { getDateAsText } from "app/UtilitiesAndConstants/UtilityFunctions/timeAndDate";
import { isAfterTrial, isSI } from "app/UtilitiesAndConstants/UtilityFunctions/usersUtils";

export type DunningsButtonClickDestination = "billing" | "package" | null;

const contactUsLink =
    "https://support.similarweb.com/hc/en-us/requests/new?ticket_form_id=17333714317725&action=KnowlegeCenter";

interface IUseDunningsApi {
    isHomePageModalDiscarded: boolean;
    isInRetryProcess: boolean;
    isAfterTrialDunnings: boolean;
    isAfterTrialDunningsWithTrial: boolean;
    isAfterTrialDunningsWithoutTrial: boolean;
    isLastPaymentFailedWarning: boolean;
    hasDunnings: boolean;
    hasDunningsBanner: boolean;
    numberOfFailedPayments: number;
    onHomePageModalDiscard: () => void;
    onContactUsClicked: (eventName: string) => void;
    onDunningsButtonClicked: (eventName: string, destination?: DunningsButtonClickDestination) => void;
}

export const useDunnings = (): IUseDunningsApi => {
    const { state } = useCurrentStateAndParams();
    const { getLocalStorage, setLocalStorage } = useLocalStorage();
    const todayAsText = getDateAsText();
    const [isHomePageModalDiscarded, setIsHomePageModalDiscarded] = useState(
        getLocalStorage(DUNNINGS_MODAL_LOCAL_STORAGE_KEY) === todayAsText,
    );

    const isDunningsEnabled = !isSI(swSettings);
    // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
    const isPaymentFailed = swSettings?.components?.Home?.resources?.IsPaymentFailed === true;
    const isLastPaymentFailedWarning = swSettings?.components?.Home?.resources?.IsLastPaymentFailedWarning === true;
    const numberOfFailedPayments: number = swSettings?.components?.Home?.resources?.NumberOfFailedPayments || 0;
    const isAfterTrialUser = isAfterTrial(swSettings);

    const isInRetryProcess = useMemo(
        (): boolean => isDunningsEnabled && isPaymentFailed && !isAfterTrialUser,
        [isDunningsEnabled, isPaymentFailed, isAfterTrialUser],
    );

    const canHaveTrial = shouldSeeReactivationBanner();

    const isAfterTrialDunnings = useMemo(
        (): boolean => isDunningsEnabled && isPaymentFailed && isAfterTrialUser,
        [isDunningsEnabled, isPaymentFailed, isAfterTrialUser],
    );

    const isAfterTrialDunningsWithTrial = useMemo(
        (): boolean => isAfterTrialDunnings && canHaveTrial,
        [canHaveTrial, isAfterTrialDunnings],
    );

    const isAfterTrialDunningsWithoutTrial = useMemo(
        (): boolean => isAfterTrialDunnings && !canHaveTrial,
        [canHaveTrial, isAfterTrialDunnings],
    );

    const hasDunnings = useMemo(
        () => isInRetryProcess || isAfterTrialDunnings,
        [isInRetryProcess, isAfterTrialDunnings],
    );

    const hasDunningsBanner = useMemo(
        () => CI_PAGE_COLLECTION.some((p) => isOnPage({ current: state.name, page: p })) && hasDunnings,
        [hasDunnings, state.name],
    );

    const onHomePageModalDiscard = useCallback(() => {
        setLocalStorage(DUNNINGS_MODAL_LOCAL_STORAGE_KEY, todayAsText);
        setIsHomePageModalDiscarded(true);
    }, [setLocalStorage, todayAsText]);

    const onContactUsClicked = useCallback(
        (eventName: string): void => {
            allTrackers.dispatchTrackEvent("button", "click", `${eventName}/contact us/${numberOfFailedPayments}`);
            window.open(contactUsLink);
        },
        [numberOfFailedPayments],
    );

    const onDunningsButtonClicked = useCallback(
        (eventName: string, destination: DunningsButtonClickDestination = null): void => {
            allTrackers.dispatchTrackEvent("button", "click", `${eventName}/${numberOfFailedPayments}`);

            switch (destination) {
                case "package":
                    window.open(`${swSettings.swsites.login}/journey/packages-global`);
                    return;
                case "billing":
                    openBillingInformationPage();
                    return;
            }
        },
        [numberOfFailedPayments],
    );

    return {
        isHomePageModalDiscarded,
        isInRetryProcess,
        isAfterTrialDunnings,
        isAfterTrialDunningsWithTrial,
        isAfterTrialDunningsWithoutTrial,
        isLastPaymentFailedWarning,
        hasDunnings,
        hasDunningsBanner,
        numberOfFailedPayments,
        onHomePageModalDiscard,
        onContactUsClicked,
        onDunningsButtonClicked,
    };
};
